import { LanguageSelector, usePreferences } from '@xhubio-saas/core-frontend/kernel'
import type { Theme } from '@xhubio-saas/core-frontend/ui-components'
import { cn, useTheme } from '@xhubio-saas/core-frontend/ui-components'
import { Check, Loader2, Monitor, Moon, Sun } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { SettingsLayout } from '../../../layouts/SettingsLayout'
import { FIELD_RING } from '../../auth/authUi'

interface ThemeOptionProps {
  theme: Theme
  currentTheme: Theme
  icon: React.ReactNode
  label: string
  description: string
  onClick: () => void
  isUpdating?: boolean
}

function ThemeOption({
  theme,
  currentTheme,
  icon,
  label,
  description,
  onClick,
  isUpdating,
}: ThemeOptionProps) {
  const isSelected = theme === currentTheme

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={isUpdating}
      className={cn(
        // Name the properties instead of `transition-all`: the latter animates
        // every animatable property, including the focus indicator — a keyboard
        // user would watch their focus ring fade in rather than see it at once.
        'relative flex flex-col items-center gap-3 rounded-lg border-2 p-6 text-left transition-[background-color,border-color] duration-150 hover:bg-accent disabled:opacity-50 cursor-pointer',
        // The app's 2px focus ring instead of the UA default outline these raw
        // buttons fell back to — visible either way, consistent only this way.
        FIELD_RING,
        isSelected ? 'border-primary bg-accent' : 'border-muted'
      )}
    >
      {isSelected && (
        <div className="absolute right-2 top-2">
          {isUpdating ? (
            <Loader2 className="size-5 text-primary animate-spin" />
          ) : (
            <Check className="size-5 text-primary" />
          )}
        </div>
      )}
      <div
        className={cn(
          'flex size-12 items-center justify-center rounded-full',
          isSelected ? 'bg-primary text-primary-foreground' : 'bg-muted'
        )}
      >
        {icon}
      </div>
      <div className="text-center">
        <h3 className="font-medium">{label}</h3>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
    </button>
  )
}

/**
 * Settings appearance page - Theme selection
 * Saves theme preference to backend for cross-device sync
 */
export function SettingsAppearancePage() {
  const { t } = useTranslation()
  const { theme, setTheme } = useTheme()
  const { setPreference, isUpdating } = usePreferences()

  /**
   * Handle theme change - updates both local state and backend
   */
  const handleThemeChange = async (newTheme: Theme) => {
    // Update local theme immediately for instant feedback
    setTheme(newTheme)
    // Save to backend (async)
    await setPreference('theme', newTheme)
  }

  return (
    <SettingsLayout>
      <div className="space-y-6">
        <div>
          <h2 className="text-xl font-semibold">{t('settings.appearanceTitle', 'Appearance')}</h2>
          <p className="text-sm text-muted-foreground">
            {t('settings.appearanceDescription', 'Customize the appearance of the application.')}
          </p>
        </div>

        <div className="space-y-4">
          <h3 className="text-sm font-medium">{t('settings.themeLabel', 'Theme')}</h3>
          <div className="grid gap-4 sm:grid-cols-3">
            <ThemeOption
              theme="light"
              currentTheme={theme}
              icon={<Sun className="size-6" />}
              label={t('settings.themeLight', 'Light')}
              description={t(
                'settings.themeLightDescription',
                'Light mode for bright environments'
              )}
              onClick={() => handleThemeChange('light')}
              isUpdating={isUpdating && theme === 'light'}
            />
            <ThemeOption
              theme="dark"
              currentTheme={theme}
              icon={<Moon className="size-6" />}
              label={t('settings.themeDark', 'Dark')}
              description={t(
                'settings.themeDarkDescription',
                'Dark mode for low-light environments'
              )}
              onClick={() => handleThemeChange('dark')}
              isUpdating={isUpdating && theme === 'dark'}
            />
            <ThemeOption
              theme="system"
              currentTheme={theme}
              icon={<Monitor className="size-6" />}
              label={t('settings.themeSystem', 'System')}
              description={t('settings.themeSystemDescription', 'Follow your system settings')}
              onClick={() => handleThemeChange('system')}
              isUpdating={isUpdating && theme === 'system'}
            />
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="text-sm font-medium">{t('settings.languageLabel', 'Language')}</h3>
          <p className="text-sm text-muted-foreground">
            {t('settings.languageDescription', 'Select your preferred language for the interface.')}
          </p>
          <LanguageSelector />
        </div>
      </div>
    </SettingsLayout>
  )
}
